import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getPlanAiStatus,
  getPlanByMonth,
  listPlans,
  plansByHotelKey,
  triggerTopics,
  updatePlan,
  type MonthlyPlan,
  type PlanAiStatus,
} from '../features/plans/api.js';
import {
  autoGenerate,
  createSlot,
  listPosts,
  rotationWarnings,
  updatePost,
  type Post,
  type RotationWarning,
} from '../features/posts/api.js';
import { getHotel, listCalendar, type CalendarEntry, type Hotel } from '../features/hotels/api.js';
import {
  calendarFailureDetail,
  describeCalendarFailures,
  postsMissing,
  resolveCalendarState,
} from '../features/plans/calendar-load-model.js';
import { ListLoadError, ListStaleWarning } from '../features/plans/load-error-notice.js';
import { recordRecentItem } from '../features/navigation/recent-items.js';
import { CalendarMonth } from '../features/posts/calendar-month.js';
import { CalendarWeek } from '../features/posts/calendar-week.js';
import { EditorDrawer } from '../features/posts/editor-drawer.js';
import { useOpenPost } from '../features/posts/use-open-post.js';
import { BulkBar } from '../features/posts/bulk-bar.js';
import { WorkflowSteps } from '../features/posts/workflow-help.js';
import { stageFor } from '../features/posts/stage-color.js';
import { Card } from '../components/ui/card.js';
import { Button } from '../components/ui/button.js';
import { cn } from '../lib/utils.js';
import { ApprovalBatchesPanel } from '../features/approvals/ApprovalBatchesPanel.js';
import { ReservePickerOverlay } from '../features/reserve/reserve-picker-overlay.js';
import { useAiConfirmGuard } from '../features/ai/use-ai-confirm-guard.js';
import {
  STATUS_ICON,
  SettingsIcon,
  FilesIcon,
  CheckIcon,
  WarnIcon,
  type StatusIconKey,
} from '../components/ui/icons.js';
import { useConfirm } from '../components/ui/confirm-dialog.js';

/** Step one calendar month forward (+1) or back (-1), handling year rollover. */
/**
 * React Query's `error` is typed `Error | null` but a thrown non-Error still lands
 * there at runtime, so normalise rather than cast.
 */
function asError(isError: boolean, error: unknown): Error | null {
  if (!isError) return null;
  return error instanceof Error ? error : new Error('ismeretlen hiba');
}

function stepMonth(year: number, month: number, delta: number): { year: number; month: number } {
  const zero = year * 12 + (month - 1) + delta;
  return { year: Math.floor(zero / 12), month: (zero % 12) + 1 };
}

/** Inclusive day count between two YYYY-MM-DD strings. */
function daysBetween(startIso: string, endIso: string): number {
  const ms =
    new Date(`${endIso}T00:00:00Z`).getTime() - new Date(`${startIso}T00:00:00Z`).getTime();
  return Math.round(ms / 86_400_000) + 1;
}

/**
 * Returns the Europe/Budapest offset in minutes for a given instant.
 * +120 in summer (CEST), +60 in winter (CET). Uses Intl.DateTimeFormat
 * so we don't have to encode DST rules manually.
 */
function budapestOffsetMinutes(at: Date): number {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Europe/Budapest',
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const parts = Object.fromEntries(dtf.formatToParts(at).map((p) => [p.type, p.value]));
  const asLocalUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour === '24' ? '00' : parts.hour),
    Number(parts.minute),
    Number(parts.second),
  );
  return Math.round((asLocalUtc - at.getTime()) / 60_000);
}

const HU_MONTH_LONG = [
  'január',
  'február',
  'március',
  'április',
  'május',
  'június',
  'július',
  'augusztus',
  'szeptember',
  'október',
  'november',
  'december',
];

function PlanCalendarPage() {
  const params = Route.useParams();
  const hotelId = params.hotelId;
  const year = Number(params.year);
  const month = Number(params.month);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const guard = useAiConfirmGuard();
  const confirm = useConfirm();
  const search = Route.useSearch();
  const [view, setView] = useState<'month' | 'week'>('month');
  const [reservePickerOpen, setReservePickerOpen] = useState(false);
  const {
    openPostId,
    setOpenPostId,
    close: closeDrawer,
  } = useOpenPost(search.post, () => {
    // Ugyanarra a csengő-linkre kattintva újra nyisson: a keresés törlődik.
    void navigate({ to: '.', search: {}, replace: true });
  });
  // The editor opens with the status panel expanded (card menu on a post that
  // is with the client or already published).
  const [statusPanelOpen, setStatusPanelOpen] = useState(search.statusPanel === true);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  // Transient AI feedback toast: start / finished / failed. Errors linger
  // longer so they can't be missed while the user looks elsewhere.
  const [aiToast, setAiToast] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);
  useEffect(() => {
    if (!aiToast) return;
    const t = setTimeout(() => setAiToast(null), aiToast.kind === 'error' ? 10_000 : 6_000);
    return () => clearTimeout(t);
  }, [aiToast]);
  const toggleSelection = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  // Esc clears the selection — matches the rest of the app.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && selectedIds.size > 0 && !openPostId) {
        setSelectedIds(new Set());
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedIds.size, openPostId]);

  const hotel = useQuery<Hotel>({
    queryKey: ['hotel', hotelId],
    queryFn: () => getHotel(hotelId),
  });
  const plan = useQuery<MonthlyPlan | null>({
    queryKey: ['plan-by-month', hotelId, year, month],
    queryFn: () => getPlanByMonth(hotelId, year, month),
  });

  // Push to the sidebar MRU once both the plan + hotel name are loaded.
  useEffect(() => {
    if (!plan.data || !hotel.data) return;
    const monthLabel = new Date(year, month - 1, 1).toLocaleDateString('hu-HU', {
      year: 'numeric',
      month: 'long',
    });
    recordRecentItem({
      kind: 'plan',
      id: plan.data.id,
      label: monthLabel,
      context: hotel.data.name,
      to: `/app/plans/${hotelId}/${year}/${month}`,
    });
  }, [plan.data, hotel.data, hotelId, year, month]);
  const posts = useQuery<Post[]>({
    queryKey: ['posts', plan.data?.id],
    queryFn: () => (plan.data ? listPosts(plan.data.id) : Promise.resolve([])),
    enabled: !!plan.data,
  });
  const warnings = useQuery<RotationWarning[]>({
    queryKey: ['posts-warnings', plan.data?.id],
    queryFn: () => (plan.data ? rotationWarnings(plan.data.id) : Promise.resolve([])),
    enabled: !!plan.data,
  });
  // Holiday + hotel-event chips painted across the calendar header. The
  // range is the plan period itself so we don't haul in entries that
  // wouldn't be visible.
  const calendar = useQuery<CalendarEntry[]>({
    queryKey: ['hotel-calendar', hotelId, plan.data?.startDate, plan.data?.endDate],
    queryFn: () =>
      plan.data?.startDate && plan.data?.endDate
        ? listCalendar(hotelId, plan.data.startDate, plan.data.endDate)
        : Promise.resolve([]),
    enabled: !!plan.data?.startDate && !!plan.data?.endDate,
  });

  // User-initiated reload of everything this page reads. A mutation (not
  // `isFetching`) so the button's pending label means "you clicked it", and an
  // explicit refetch rather than an automatic retry — the retry policy in
  // lib/query-retry.ts deliberately refuses to auto-retry a 429.
  const retryCalendar = useMutation({
    mutationFn: () =>
      Promise.all([
        qc.refetchQueries({ queryKey: ['plan-by-month', hotelId, year, month] }),
        qc.refetchQueries({ queryKey: ['hotel', hotelId] }),
        qc.refetchQueries({ queryKey: ['posts', plan.data?.id] }),
        qc.refetchQueries({ queryKey: ['posts-warnings', plan.data?.id] }),
        qc.refetchQueries({ queryKey: ['hotel-calendar', hotelId] }),
      ]),
  });

  const move = useMutation({
    mutationFn: ({ postId, newIso }: { postId: string; newIso: string }) =>
      updatePost(postId, { scheduledFor: newIso }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['posts', plan.data?.id] });
      void qc.invalidateQueries({ queryKey: ['posts-warnings', plan.data?.id] });
    },
    // Drag&drop has no optimistic update, so on failure the card snaps back to
    // its original slot. Surface why so the move doesn't appear to silently
    // undo itself.
    onError: () => {
      void qc.invalidateQueries({ queryKey: ['posts', plan.data?.id] });
    },
  });

  const auto = useMutation({
    mutationFn: () => autoGenerate(plan.data!.id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['posts', plan.data?.id] });
    },
  });

  /** Create a slot on a specific calendar day at 10:00 Europe/Budapest.
   * We don't hardcode the UTC offset because of DST — the user wants
   * "10 in the morning" to mean 10 local time year-round. Intl APIs give
   * us the right offset for the target date. */
  const addOnDay = useMutation({
    mutationFn: (dayIso: string) => {
      const offsetMin = budapestOffsetMinutes(new Date(`${dayIso}T10:00:00Z`));
      // Build an instant that is 10:00 in Europe/Budapest by subtracting
      // the local offset from a naive UTC-10:00 timestamp.
      const utcMs = Date.UTC(
        Number(dayIso.slice(0, 4)),
        Number(dayIso.slice(5, 7)) - 1,
        Number(dayIso.slice(8, 10)),
        10,
        0,
        0,
      );
      return createSlot(plan.data!.id, {
        scheduledFor: new Date(utcMs - offsetMin * 60_000).toISOString(),
        brandOrSales: 'brand',
        publishToFb: true,
        publishToIg: true,
      });
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['posts', plan.data?.id] }),
  });

  /** Widen the plan's [startDate, endDate] to include `dayIso`, then create a
   * slot on that day. Used when the user clicks a + on a day that falls
   * outside the current plan range but still inside the rendered month grid.
   * We update the range first so the freshly-created slot lands inside the
   * (now wider) period and renders in-range immediately. */
  const addOnDayExtending = useMutation({
    mutationFn: async (dayIso: string) => {
      const p = plan.data!;
      const newStart = dayIso < p.startDate ? dayIso : p.startDate;
      const newEnd = dayIso > p.endDate ? dayIso : p.endDate;
      if (newStart !== p.startDate || newEnd !== p.endDate) {
        await updatePlan(p.id, { startDate: newStart, endDate: newEnd });
      }
      const offsetMin = budapestOffsetMinutes(new Date(`${dayIso}T10:00:00Z`));
      const utcMs = Date.UTC(
        Number(dayIso.slice(0, 4)),
        Number(dayIso.slice(5, 7)) - 1,
        Number(dayIso.slice(8, 10)),
        10,
        0,
        0,
      );
      return createSlot(p.id, {
        scheduledFor: new Date(utcMs - offsetMin * 60_000).toISOString(),
        brandOrSales: 'brand',
        publishToFb: true,
        publishToIg: true,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['plan-by-month', hotelId, year, month] });
      void qc.invalidateQueries({ queryKey: ['posts', plan.data?.id] });
    },
  });

  // Poll the in-flight state of any topic-ideation job tied to this plan so
  // the AI button can show "Generálás folyamatban…" and stay disabled while
  // a job is waiting/active/delayed in BullMQ. 3s cadence is responsive but
  // not chatty — these calls hit Redis directly, not the AI provider.
  const aiStatus = useQuery<PlanAiStatus>({
    queryKey: ['plan-ai-status', plan.data?.id],
    queryFn: () => getPlanAiStatus(plan.data!.id),
    enabled: !!plan.data,
    refetchInterval: (q) => ((q.state.data as PlanAiStatus | undefined)?.inFlight ? 3000 : 8000),
  });

  // Auto-refresh the calendar when a topic-ideation job finishes. The status
  // poll flips inFlight true→false on completion; that's our cue to pull in
  // the freshly generated topics without the user manually reloading — and to
  // tell the user how the run ended (the same status payload carries the
  // terminal outcome of the plan-wide job in `lastFinished`).
  const prevAiInFlight = useRef(false);
  const prevAiJobId = useRef<string | null>(null);
  useEffect(() => {
    const inFlight = aiStatus.data?.inFlight ?? false;
    if (prevAiInFlight.current && !inFlight && plan.data?.id) {
      void qc.invalidateQueries({ queryKey: ['posts', plan.data.id] });
      void qc.invalidateQueries({ queryKey: ['posts-warnings', plan.data.id] });
      const fin = aiStatus.data?.lastFinished ?? null;
      if (fin && fin.jobId === prevAiJobId.current) {
        setAiToast(
          fin.state === 'completed'
            ? {
                kind: 'success',
                text: 'Az AI témajavaslat elkészült — a javaslatok megjelentek a naptárban.',
              }
            : {
                kind: 'error',
                text: 'Az AI témajavaslat hibára futott. Próbáld újra, vagy nézd meg a Folyamatok oldalt.',
              },
        );
      } else {
        // Per-post (variáció) runs end here too — no per-job outcome is
        // exposed for those, so confirm the finish neutrally.
        setAiToast({ kind: 'success', text: 'Az AI generálás befejeződött.' });
      }
    }
    if (inFlight) prevAiJobId.current = aiStatus.data?.jobId ?? null;
    prevAiInFlight.current = inFlight;
  }, [aiStatus.data, qc, plan.data?.id]);

  const aiTopics = useMutation({
    mutationFn: () => triggerTopics(plan.data!.id),
    onSuccess: () => {
      setAiToast({
        kind: 'success',
        text: 'AI témajavaslat elindítva — kb. 30–60 mp, a javaslatok automatikusan megjelennek.',
      });
      void qc.invalidateQueries({ queryKey: ['posts', plan.data?.id] });
      void qc.invalidateQueries({ queryKey: ['plan-ai-status', plan.data?.id] });
    },
    onError: () => {
      // Re-check status — backend rejected (likely because another job is
      // already in-flight), so we want the UI to reflect that immediately.
      void qc.invalidateQueries({ queryKey: ['plan-ai-status', plan.data?.id] });
    },
  });

  // Aggregated stats for the AI hint strip: how many slots already have a
  // topic, how many are waiting for one, how many slots total. Drives both
  // the chip strip under the headline and the "AI témajavaslat" button hint.
  const stats = useMemo(() => {
    const list = posts.data ?? [];
    const withTopic = list.filter((p) => p.selectedTopicTitle).length;
    const suggestions = list.filter((p) => !p.selectedTopicTitle && p.hasTopicSuggestions).length;
    const empty = list.length - withTopic - suggestions;
    return { total: list.length, withTopic, suggestions, empty };
  }, [posts.data]);

  // Per-day post counts (YYYY-MM-DD → n), so the reserve picker can mark which
  // days already hold scheduled content.
  const filledDays = useMemo(() => {
    const acc: Record<string, number> = {};
    for (const p of posts.data ?? []) {
      const d = p.scheduledFor.slice(0, 10);
      acc[d] = (acc[d] ?? 0) + 1;
    }
    return acc;
  }, [posts.data]);

  const monthName = HU_MONTH_LONG[month - 1] ?? String(month);

  // All plans for this hotel — only needed to power the "extend previous
  // plan" action in the no-plan branch. Cheap (one row per month) and shares
  // the same cache key listPlans uses elsewhere. Hook lives above the early
  // return so React's hook order stays stable across has-plan / no-plan.
  const plans = useQuery<MonthlyPlan[]>({
    queryKey: plansByHotelKey(hotelId),
    queryFn: () => listPlans(hotelId),
  });

  // The most recent plan strictly BEFORE (year, month). listPlans already
  // returns rows ordered by (year, month) DESC, but we sort defensively so
  // we don't depend on backend ordering. Used as the extend target.
  const previousPlan = useMemo<MonthlyPlan | null>(() => {
    const earlier = (plans.data ?? []).filter(
      (p) => p.year < year || (p.year === year && p.month < month),
    );
    earlier.sort((a, b) => b.year - a.year || b.month - a.month);
    return earlier[0] ?? null;
  }, [plans.data, year, month]);

  // Extend the previous plan's period so it covers the current month. We push
  // its endDate forward to the last day of (year, month). NOTE: the backend
  // /plans/by-month resolves strictly on (year, month), so this alone will
  // NOT make the current month surface that plan — see the report for the
  // required backend change. We still wire the PATCH per spec.
  const extendPrev = useMutation({
    mutationFn: () => {
      if (!previousPlan) throw new Error('Nincs korábbi terv');
      // Date.UTC(year, month, 0) → last day of (month), month is 1-based here.
      const lastDay = new Date(Date.UTC(year, month, 0)).toISOString().slice(0, 10);
      return updatePlan(previousPlan.id, { endDate: lastDay });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['plan-by-month', hotelId, year, month] });
      void qc.invalidateQueries({ queryKey: plansByHotelKey(hotelId) });
      if (previousPlan) {
        void qc.invalidateQueries({
          queryKey: ['plan-by-month', hotelId, previousPlan.year, previousPlan.month],
        });
      }
    },
  });

  // "Failed to load the plan" must never render as "this month has no plan" —
  // that invited the user to create a duplicate of a plan that already exists.
  const loadState = resolveCalendarState({
    planLoading: plan.isLoading,
    planError: asError(plan.isError, plan.error),
    hasPlan: !!plan.data,
    hotelError: asError(hotel.isError, hotel.error),
    postsError: asError(posts.isError, posts.error),
    warningsError: asError(warnings.isError, warnings.error),
    calendarError: asError(calendar.isError, calendar.error),
  });
  const failureSubjects = describeCalendarFailures(loadState.failures);
  const failureDetail = calendarFailureDetail(loadState.failures);
  const retryLoad = () => retryCalendar.mutate();

  if (loadState.kind === 'loading') return <p>Betöltés…</p>;
  if (loadState.kind === 'error')
    return (
      <div className="space-y-5">
        <nav className="text-body text-[var(--fg-2)]">
          <Link to="/app/plans" className="hover:text-[var(--ink-700)] hover:underline">
            Tervek
          </Link>
          <span className="mx-2 text-[var(--fg-faint)]">›</span>
          <span className="text-[var(--ink-700)]">
            {year}. {monthName}
          </span>
        </nav>
        <ListLoadError
          subjects={failureSubjects ?? 'a tervet'}
          detail={failureDetail}
          onRetry={retryLoad}
          retrying={retryCalendar.isPending}
        />
      </div>
    );
  // Equivalent to `loadState.kind === 'no-plan'` at this point — the error branch
  // above already returned — but phrased on `plan.data` so TypeScript narrows it
  // for the rest of the component. The ordering is the fix: a load failure can no
  // longer fall through into this "no plan for this month" screen.
  if (!plan.data)
    return (
      <div className="space-y-5">
        {/* Breadcrumb — identical to the has-plan branch. */}
        <nav className="text-body text-[var(--fg-2)]">
          <Link to="/app/plans" className="hover:text-[var(--ink-700)] hover:underline">
            Tervek
          </Link>
          <span className="mx-2 text-[var(--fg-faint)]">›</span>
          {hotel.data && (
            <>
              <Link
                to="/app/hotels/$hotelId"
                params={{ hotelId }}
                className="hover:text-[var(--ink-700)] hover:underline"
              >
                {hotel.data.name}
              </Link>
              <span className="mx-2 text-[var(--fg-faint)]">›</span>
            </>
          )}
          <span className="text-[var(--ink-700)]">
            {year}. {monthName}
          </span>
        </nav>

        {/* HERO header — same jumbo month as the has-plan branch. There's no
            plan yet, so the date-range line is replaced with an empty-state
            note and the two function cards render visually muted + disabled to
            convey "these light up once a plan exists". */}
        <div className="rounded-lg border border-[var(--app-divider)] bg-[var(--app-surface)] p-5">
          <div className="flex flex-wrap items-start justify-between gap-5">
            <div className="min-w-0 flex-1">
              <div className="h2-eyebrow">Havi terv</div>
              <h1 className="h2-month-jumbo mt-1.5">
                <span className="h2-year">{year}.</span>{' '}
                <span className="h2-italic">{monthName}</span>
              </h1>
              <div className="mt-1.5 text-meta text-[var(--fg-2)]">
                <span className="font-semibold tn">
                  {year}-{String(month).padStart(2, '0')}-01
                </span>
                <span className="mx-1.5 text-[var(--fg-faint)]">→</span>
                <span className="font-semibold tn">
                  {new Date(Date.UTC(year, month, 0)).toISOString().slice(0, 10)}
                </span>
              </div>
              <p className="mt-2 max-w-[60ch] text-body text-[var(--fg-2)]">
                Ehhez a hónaphoz még nincs havi terv ennél a hotelnél. Hozz létre egy új tervet,
                vagy bővítsd ki az előző havi tervet erre a hónapra.
              </p>
              {/* Stats strip — all zeros, muted, since there are no slots yet. */}
              <div className="mt-4 flex flex-wrap items-center gap-1.5 opacity-60">
                <StatChip label="összes slot" value={0} tone="muted" />
                <StatChip label="téma kiválasztva" value={0} tone="muted" />
                <StatChip label="témaválasztásra vár" value={0} tone="muted" />
                <StatChip label="teljesen üres" value={0} tone="muted" />
              </div>
            </div>
            {/* Side panel: the same two function cards, but greyed out +
                disabled — they only become usable once a plan exists. */}
            <div
              className="flex w-full flex-col gap-3 opacity-60 sm:flex-row lg:w-[560px] lg:flex-shrink-0"
              aria-disabled="true"
            >
              {/* Card 1 — Auto slot generation (disabled). */}
              <div className="flex min-h-[180px] flex-1 flex-col rounded-md border border-[var(--app-divider)] bg-[var(--app-surface-2)] p-4">
                <div className="flex items-center gap-2">
                  <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[var(--app-divider)] text-meta font-bold text-[var(--ink-700)]">
                    <SettingsIcon size={12} />
                  </span>
                  <span className="text-body font-semibold text-[var(--ink-700)]">
                    Auto-generálás
                  </span>
                </div>
                <p className="mt-2 flex-1 text-body leading-snug text-[var(--fg-2)]">
                  A hotel heti gyakorisága alapján automatikusan kitölti az időszakot üres
                  slotokkal.
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  className="mt-3 w-full whitespace-nowrap"
                  disabled
                >
                  Slotok generálása
                </Button>
              </div>

              {/* Card 2 — AI topic suggestions (disabled). */}
              <div className="flex min-h-[180px] flex-1 flex-col rounded-md border border-[var(--brand-200)] bg-[var(--ai-bg)] p-4">
                <div className="flex items-center gap-2">
                  <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[var(--brand-100)] text-meta font-bold text-[var(--brand-800)]">
                    AI
                  </span>
                  <span className="text-body font-semibold text-[var(--ink-700)]">
                    AI témajavaslat
                  </span>
                </div>
                <p className="mt-2 flex-1 text-body leading-snug text-[var(--fg-2)]">
                  Terv létrehozása után az AI a teljesen üres slotokra témákat javasol.
                </p>
                <Button size="sm" className="mt-3 w-full whitespace-nowrap" disabled>
                  Új téma az üres slotokra
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Toolbar: same view-toggle + month pager row as the has-plan branch,
            plus the two create/extend actions. */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1 rounded-full border border-[var(--app-divider)] bg-[var(--app-surface)] p-1 w-fit">
            <ToolbarToggle active={view === 'month'} onClick={() => setView('month')}>
              Havi
            </ToolbarToggle>
            <ToolbarToggle active={view === 'week'} onClick={() => setView('week')}>
              Heti
            </ToolbarToggle>
          </div>
          <MonthNav hotelId={hotelId} year={year} month={month} label={`${year}. ${monthName}`} />
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              onClick={() => navigate({ to: '/app/plans/new', search: { hotelId, year, month } })}
            >
              Terv létrehozása
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={!previousPlan || extendPrev.isPending}
              title={
                previousPlan
                  ? `A(z) ${previousPlan.year}. ${HU_MONTH_LONG[previousPlan.month - 1] ?? previousPlan.month} terv kibővítése eddig: ${new Date(Date.UTC(year, month, 0)).toISOString().slice(0, 10)}.`
                  : 'Nincs korábbi terv, amit kibővíthetnél.'
              }
              onClick={() => extendPrev.mutate()}
            >
              {extendPrev.isPending ? 'Kibővítés…' : 'Előző havi terv kibővítése erre a hónapra'}
            </Button>
          </div>
        </div>

        {extendPrev.isError && (
          <div className="flex items-start gap-1.5 rounded-md bg-[var(--st-rejected-bg)] px-3 py-2 text-meta text-[var(--danger)]">
            <span className="flex-1">{(extendPrev.error as Error).message}</span>
          </div>
        )}

        {/* Empty, muted calendar grid — the full month day-grid renders so
            paging into an empty month still looks like the real page. No posts,
            no interactions (pointer-events-none + reduced opacity). */}
        <Card>
          <div className="opacity-60 pointer-events-none select-none" aria-disabled="true">
            <CalendarMonth
              year={year}
              month={month}
              posts={[]}
              warnings={[]}
              onMove={() => {}}
              onSelectPost={() => {}}
              calendarEntries={[]}
            />
          </div>
        </Card>
      </div>
    );

  const initialDate = new Date(Date.UTC(year, month - 1, 1));

  return (
    <div className="space-y-5">
      {/* Breadcrumb */}
      <nav className="text-body text-[var(--fg-2)]">
        <Link to="/app/plans" className="hover:text-[var(--ink-700)] hover:underline">
          Tervek
        </Link>
        <span className="mx-2 text-[var(--fg-faint)]">›</span>
        {hotel.data && (
          <>
            <Link
              to="/app/hotels/$hotelId"
              params={{ hotelId }}
              className="hover:text-[var(--ink-700)] hover:underline"
            >
              {hotel.data.name}
            </Link>
            <span className="mx-2 text-[var(--fg-faint)]">›</span>
          </>
        )}
        <span className="text-[var(--ink-700)]">
          {year}. {monthName}
        </span>
      </nav>

      {/* The plan loaded, but something else on the page did not. Non-blocking —
          the calendar is still usable — but it must be visible: an empty grid
          caused by a failed post fetch is indistinguishable from a plan with no
          slots, which is what "the data doesn't get in" looked like. */}
      {failureSubjects && (
        <ListStaleWarning
          subjects={failureSubjects}
          detail={
            postsMissing(loadState.failures)
              ? [failureDetail, 'A naptár üresebbnek tűnhet, mint amennyi valójában van']
                  .filter(Boolean)
                  .join(' — ')
              : failureDetail
          }
          onRetry={retryLoad}
          retrying={retryCalendar.isPending}
        />
      )}

      {/* HERO header — editorial jumbo month + brief + side actions */}
      <div className="rounded-lg border border-[var(--app-divider)] bg-[var(--app-surface)] p-5">
        <div className="flex flex-wrap items-start justify-between gap-5">
          <div className="min-w-0 flex-1">
            <div className="h2-eyebrow">Havi terv</div>
            <h1 className="h2-month-jumbo mt-1.5">
              <span className="h2-year">{year}.</span>{' '}
              <span className="h2-italic">{monthName}</span>
            </h1>
            {plan.data.startDate && plan.data.endDate && (
              <div className="mt-1.5 text-meta text-[var(--fg-2)]">
                <span className="font-semibold tn">{plan.data.startDate}</span>
                <span className="mx-1.5 text-[var(--fg-faint)]">→</span>
                <span className="font-semibold tn">{plan.data.endDate}</span>
                <span className="ml-2 text-[var(--fg-faint)]">
                  ({daysBetween(plan.data.startDate, plan.data.endDate)} nap)
                </span>
              </div>
            )}
            <p className="mt-2 max-w-[60ch] text-body text-[var(--fg-2)]">
              {plan.data.brief ?? (
                <span className="italic text-[var(--fg-faint)]">Nincs brief megadva.</span>
              )}
            </p>
            {/* Stats strip */}
            <div className="mt-4 flex flex-wrap items-center gap-1.5">
              <StatChip label="összes slot" value={stats.total} />
              <StatChip label="téma kiválasztva" value={stats.withTopic} tone="done" />
              <StatChip label="témaválasztásra vár" value={stats.suggestions} tone="warn" />
              <StatChip label="teljesen üres" value={stats.empty} tone="muted" />
              {warnings.data && warnings.data.length > 0 && (
                <StatChip
                  label="rotation figyelmeztetés"
                  value={warnings.data.length}
                  tone="danger"
                />
              )}
            </div>
          </div>
          {/* Side panel: two equal-width action cards side by side.
              Width FIXED at 560px on lg+ (2×260 + 40 gap) so the jumbo
              headline doesn't reflow between 1 and 2 lines as button
              labels change. On smaller screens cards stack full-width. */}
          <div className="flex w-full flex-col gap-3 sm:flex-row lg:w-[560px] lg:flex-shrink-0">
            {/* Card 1 — Auto slot generation */}
            <div className="flex min-h-[180px] flex-1 flex-col rounded-md border border-[var(--app-divider)] bg-[var(--app-surface-2)] p-4">
              <div className="flex items-center gap-2">
                <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[var(--app-divider)] text-meta font-bold text-[var(--ink-700)]">
                  <SettingsIcon size={12} />
                </span>
                <span className="text-body font-semibold text-[var(--ink-700)]">
                  Auto-generálás
                </span>
              </div>
              <p className="mt-2 flex-1 text-body leading-snug text-[var(--fg-2)]">
                A hotel heti gyakorisága alapján automatikusan kitölti az időszakot üres slotokkal.
              </p>
              <Button
                size="sm"
                variant="outline"
                className="mt-3 w-full whitespace-nowrap"
                onClick={() => {
                  if (
                    !guard.confirmIfRepeated(
                      `plan-${plan.data?.id ?? hotelId}-auto-slots`,
                      'Auto-generálás',
                    )
                  )
                    return;
                  auto.mutate();
                }}
                disabled={auto.isPending}
              >
                {auto.isPending ? 'Generálás…' : 'Slotok generálása'}
              </Button>
            </div>

            {/* Card 2 — AI topic suggestions */}
            <div className="flex min-h-[180px] flex-1 flex-col rounded-md border border-[var(--brand-200)] bg-[var(--ai-bg)] p-4">
              <div className="flex items-center gap-2">
                <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[var(--brand-100)] text-meta font-bold text-[var(--brand-800)]">
                  AI
                </span>
                <span className="text-body font-semibold text-[var(--ink-700)]">
                  AI témajavaslat
                </span>
                {aiStatus.data?.inFlight && (
                  <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-[var(--brand-100)] px-2 py-0.5 text-meta font-semibold text-[var(--brand-800)]">
                    <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[var(--brand-600)]" />
                    {aiStatus.data.state === 'active'
                      ? 'Fut'
                      : aiStatus.data.state === 'waiting'
                        ? 'Várakozik'
                        : 'Időzítve'}
                  </span>
                )}
              </div>
              {/* Helper text — pinned min-height so the card (and the hero)
                  doesn't grow/shrink as the string flips between idle and
                  "Generálás folyamatban…". */}
              <p className="mt-2 flex-1 text-body leading-snug text-[var(--fg-2)]">
                {aiStatus.data?.inFlight
                  ? 'Generálás folyamatban — kb. 30–60 mp. A javaslatok automatikusan megjelennek.'
                  : 'Csak a teljesen üres slotokra generál új témákat. A már javaslatot kapott slotokat nem érinti.'}
              </p>
              <Button
                size="sm"
                className="mt-3 w-full whitespace-nowrap"
                onClick={() => {
                  if (
                    !guard.confirmIfRepeated(
                      `plan-${plan.data?.id ?? hotelId}-topics`,
                      'AI témajavaslat',
                    )
                  )
                    return;
                  aiTopics.mutate();
                }}
                disabled={
                  aiTopics.isPending || stats.empty === 0 || (aiStatus.data?.inFlight ?? false)
                }
              >
                {aiStatus.data?.inFlight
                  ? 'Generálás folyamatban…'
                  : aiTopics.isPending
                    ? 'Indítás…'
                    : stats.empty === 0
                      ? 'Nincs üres slot'
                      : `Új téma ${stats.empty} üres slotra`}
              </Button>
              {aiTopics.isError && !aiStatus.data?.inFlight && (
                <div className="mt-2 flex items-start gap-1.5 rounded-md bg-[var(--st-rejected-bg)] px-2 py-1.5 text-meta text-[var(--danger)]">
                  <svg
                    width={12}
                    height={12}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                    className="mt-0.5 flex-shrink-0"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <path d="M12 8v4M12 16h.01" />
                  </svg>
                  <span className="flex-1">{(aiTopics.error as Error).message}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Toolbar: view toggle + month pager on the left, legend on the right —
          all on one row, sitting right above the calendar. */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1 rounded-full border border-[var(--app-divider)] bg-[var(--app-surface)] p-1 w-fit">
          <ToolbarToggle active={view === 'month'} onClick={() => setView('month')}>
            Havi
          </ToolbarToggle>
          <ToolbarToggle active={view === 'week'} onClick={() => setView('week')}>
            Heti
          </ToolbarToggle>
        </div>
        <MonthNav hotelId={hotelId} year={year} month={month} label={`${year}. ${monthName}`} />
        <div className="ml-auto flex items-center gap-3">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setReservePickerOpen(true)}
            title="Kész tartalom hozzáadása a tervhez a tartalék pool-ból"
            className="inline-flex items-center gap-1.5"
          >
            <FilesIcon size={14} />
            Tartalékból hozzáadás
          </Button>
          <StageLegend />
        </div>
      </div>

      {plan.data.startDate && plan.data.endDate && (
        <p className="text-meta text-[var(--fg-2)]">
          A terv hatóköre:{' '}
          <span className="font-semibold tn text-[var(--ink-700)]">{plan.data.startDate}</span>
          <span className="mx-1 text-[var(--fg-faint)]">→</span>
          <span className="font-semibold tn text-[var(--ink-700)]">{plan.data.endDate}</span>.{' '}
          <span className="text-[var(--fg-faint)]">
            Új poszt felvétele a tartományon kívüli napra kiterjeszti.
          </span>
        </p>
      )}

      {move.isError && (
        <div className="flex items-start gap-1.5 rounded-md bg-[var(--st-rejected-bg)] px-3 py-2 text-meta text-[var(--danger)]">
          <span className="flex-1">
            A poszt áthelyezése nem sikerült: {(move.error as Error).message}
          </span>
        </div>
      )}

      <Card>
        {view === 'month' ? (
          <CalendarMonth
            year={year}
            month={month}
            posts={posts.data ?? []}
            warnings={warnings.data ?? []}
            onMove={(postId, newIso) => move.mutate({ postId, newIso })}
            onSelectPost={(id) => {
              setStatusPanelOpen(false);
              setOpenPostId(id);
            }}
            onOpenStatusPanel={(id) => {
              setStatusPanelOpen(true);
              setOpenPostId(id);
            }}
            onCreateOnDay={async (dayIso) => {
              // Slot-collision warning — if the user is about to add a
              // 3rd+ post for the same calendar day, double-check that's
              // intentional. Two-per-day feels heavy already; three is
              // almost always a misclick.
              const existing = (posts.data ?? []).filter(
                (p) => p.scheduledFor.slice(0, 10) === dayIso,
              ).length;
              if (existing >= 2) {
                const ok = await confirm({
                  title: 'Több poszt egy napon?',
                  message: `Ezen a napon (${dayIso}) már ${existing} poszt van időzítve. Biztosan veszel fel még egyet?`,
                  confirmLabel: 'Felveszem',
                });
                if (!ok) return;
              }
              // Out-of-range day: widen the plan period to include it before
              // creating the slot. Confirm so the user knows the scope grows.
              const outOfRange =
                !!plan.data && (dayIso < plan.data.startDate || dayIso > plan.data.endDate);
              if (outOfRange) {
                const ok = await confirm({
                  title: 'Terv időszakának kiterjesztése',
                  message: `A(z) ${dayIso} kívül esik a terv jelenlegi hatókörén (${plan.data!.startDate} → ${plan.data!.endDate}). Az új poszt felvétele kiterjeszti a terv időszakát.`,
                  confirmLabel: 'Folytatás',
                });
                if (!ok) return;
                addOnDayExtending.mutate(dayIso);
                return;
              }
              addOnDay.mutate(dayIso);
            }}
            calendarEntries={calendar.data ?? []}
            {...(plan.data?.startDate ? { rangeStart: plan.data.startDate } : {})}
            {...(plan.data?.endDate ? { rangeEnd: plan.data.endDate } : {})}
            selectedIds={selectedIds}
            onToggleSelection={(id) => toggleSelection(id)}
          />
        ) : (
          <CalendarWeek
            posts={posts.data ?? []}
            warnings={warnings.data ?? []}
            onMove={(postId, newIso) => move.mutate({ postId, newIso })}
            onSelectPost={(id) => {
              setStatusPanelOpen(false);
              setOpenPostId(id);
            }}
            onOpenStatusPanel={(id) => {
              setStatusPanelOpen(true);
              setOpenPostId(id);
            }}
            selectedIds={selectedIds}
            onToggleSelection={(id) => toggleSelection(id)}
            initialDate={initialDate}
          />
        )}
      </Card>
      <ApprovalBatchesPanel
        planId={plan.data.id}
        hotelId={hotelId}
        posts={posts.data ?? []}
        defaultEmail={hotel.data?.contactEmail ?? null}
      />

      <EditorDrawer
        postId={openPostId}
        open={openPostId !== null}
        {...(openPostId !== null && openPostId === search.post && search.comments
          ? { initialCommentsTab: search.comments }
          : {})}
        statusPanelInitiallyOpen={statusPanelOpen}
        onClose={() => {
          closeDrawer();
          setStatusPanelOpen(false);
        }}
        siblings={(() => {
          // Sort posts by scheduledFor so prev/next walks the calendar
          // in chronological order, the same order the user sees on the
          // month/week view.
          const sorted = (posts.data ?? [])
            .slice()
            .sort((a, b) => a.scheduledFor.localeCompare(b.scheduledFor));
          const idx = sorted.findIndex((p) => p.id === openPostId);
          if (idx < 0) return null;
          return {
            prev: idx > 0 ? sorted[idx - 1]!.id : null,
            next: idx < sorted.length - 1 ? sorted[idx + 1]!.id : null,
            position: idx + 1,
            total: sorted.length,
          };
        })()}
        onNavigate={(id) => {
          setStatusPanelOpen(false);
          setOpenPostId(id);
        }}
      />
      <ReservePickerOverlay
        open={reservePickerOpen}
        onClose={() => setReservePickerOpen(false)}
        planId={plan.data.id}
        hotelId={hotelId}
        rangeStart={plan.data.startDate ?? `${year}-${String(month).padStart(2, '0')}-01`}
        rangeEnd={
          plan.data.endDate ?? new Date(Date.UTC(year, month, 0)).toISOString().slice(0, 10)
        }
        filledDays={filledDays}
        onInserted={() => {
          void qc.invalidateQueries({ queryKey: ['posts', plan.data?.id] });
          void qc.invalidateQueries({ queryKey: ['posts-warnings', plan.data?.id] });
        }}
      />

      <BulkBar
        selectedIds={selectedIds}
        onClear={() => setSelectedIds(new Set())}
        onAfterMutate={() => {
          // Refetch the plan slice — the calendar will pick up the
          // mutated/deleted posts. Selection is intentionally retained
          // (except on delete, where BulkBar clears it itself) so the
          // user can chain bulk operations without re-selecting.
          void qc.invalidateQueries({ queryKey: ['posts', plan.data?.id] });
          void qc.invalidateQueries({ queryKey: ['posts-warnings', plan.data?.id] });
        }}
      />

      {/* AI run feedback toast (start / finished / failed). Same transient
          pattern as the reserve picker's toast, colored by outcome. */}
      {aiToast && (
        <div className="pointer-events-none fixed bottom-6 left-1/2 z-[60] -translate-x-1/2">
          <div
            role="status"
            aria-live="polite"
            className={cn(
              'flex max-w-md items-center gap-2 rounded-full px-4 py-2 text-meta font-medium text-white shadow-lg',
              aiToast.kind === 'success' ? 'bg-[var(--brand-700)]' : 'bg-[var(--danger)]',
            )}
          >
            {aiToast.kind === 'success' ? (
              <CheckIcon size={14} className="flex-shrink-0" />
            ) : (
              <WarnIcon size={14} className="flex-shrink-0" />
            )}
            {aiToast.text}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Prev / current-label / next month switcher. Navigates to the same
 * plan route for the adjacent calendar month, handling year rollover.
 * Works regardless of whether the target month has a plan — the route
 * itself renders the empty state when none exists.
 */
function MonthNav({
  hotelId,
  year,
  month,
  label,
}: {
  hotelId: string;
  year: number;
  month: number;
  label: string;
}) {
  const navigate = useNavigate();
  const prev = stepMonth(year, month, -1);
  const next = stepMonth(year, month, 1);
  const go = (target: { year: number; month: number }) =>
    void navigate({
      to: '/app/plans/$hotelId/$year/$month',
      params: { hotelId, year: String(target.year), month: String(target.month) },
    });
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={() => go(prev)}
        aria-label="Előző hónap"
        className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-[var(--app-divider)] bg-[var(--app-surface)] text-[var(--ink-700)] hover:bg-[var(--app-hover)]"
      >
        ←
      </button>
      <span className="min-w-[140px] text-center text-body font-semibold text-[var(--ink-700)]">
        {label}
      </span>
      <button
        type="button"
        onClick={() => go(next)}
        aria-label="Következő hónap"
        className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-[var(--app-divider)] bg-[var(--app-surface)] text-[var(--ink-700)] hover:bg-[var(--app-hover)]"
      >
        →
      </button>
    </div>
  );
}

function StatChip({
  label,
  value,
  tone = 'default',
}: {
  label: string;
  value: number;
  tone?: 'default' | 'done' | 'warn' | 'muted' | 'danger';
}) {
  const tones: Record<string, string> = {
    default: 'bg-[var(--app-bg)] text-[var(--ink-700)] border-[var(--app-divider)]',
    done: 'bg-[var(--brand-50)] text-[var(--brand-700)] border-[var(--brand-200)]',
    warn: 'bg-[var(--st-topic-pending-bg)] text-[var(--st-topic-pending-fg)] border-[var(--warning)]',
    muted: 'bg-[var(--app-bg)] text-[var(--fg-faint)] border-[var(--app-divider)]',
    danger: 'bg-[var(--st-rejected-bg)] text-[var(--danger)] border-[var(--st-rejected-fg)]',
  };
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-meta font-medium ${tones[tone]}`}
    >
      <span className="font-mono font-semibold">{value}</span>
      <span className="text-meta uppercase tracking-wider opacity-80">{label}</span>
    </span>
  );
}

function ToolbarToggle({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`h-7 rounded-full px-3 text-meta font-medium transition-colors ${
        active
          ? 'bg-[var(--brand-500)] text-white'
          : 'text-[var(--fg-2)] hover:bg-[var(--app-hover)]'
      }`}
    >
      {children}
    </button>
  );
}

/**
 * Collapsible legend explaining the colour-coded left-edge bar on the
 * post cards. Mirrors the stage-color.ts TONE map so the values can't
 * drift apart. Closed by default; the bottom slot is „Posztolva”: grey is
 * only for published posts now, a past day alone no longer mutes a card.
 */
const LEGEND_ENTRIES: Array<{
  bar: string;
  label: string;
  meaning: string;
  iconKey?: StatusIconKey;
  /** Icon colour; falls back to the neutral foreground. */
  iconColor?: string;
}> = [
  {
    // Téma piszkozat: saját lila tónus; matches stage-color.ts topic_draft.
    bar: stageFor('draft_topic').bar,
    iconColor: stageFor('draft_topic').bar,
    iconKey: 'pencil',
    label: 'Téma piszkozat',
    meaning: 'Nálunk van: a téma még piszkozat.',
  },
  {
    // D1: tartalom piszkozat + Ügyfélnél share the neutral trio; matches stage-color.ts.
    bar: 'var(--st-neutral-dot)',
    iconKey: 'pencil',
    label: 'Tartalom piszkozat',
    meaning: 'Nálunk van: tartalom piszkozat, AI-generálás vagy belső ellenőrzés.',
  },
  {
    bar: 'var(--st-ready-dot)',
    iconKey: 'check',
    label: 'Manuális közzététel',
    meaning: 'Kész a poszt, kézzel kell közzétenni, vagy mehet a Meta-ütemezőbe.',
  },
  {
    // D1: Ügyfélnél shares the neutral draft trio — distinguished by icon, not hue.
    bar: 'var(--st-neutral-dot)',
    iconKey: 'mail',
    label: 'Ügyfélnél',
    meaning: 'Kiküldve az ügyfélnek jóváhagyásra (téma vagy tartalom).',
  },
  {
    bar: 'var(--st-topic-approved-dot)',
    label: 'Téma jóváhagyva',
    meaning: 'Az ügyfél a témát jóváhagyta.',
  },
  {
    bar: 'var(--st-content-review-fg)',
    label: 'Tartalom jóváhagyva',
    meaning: 'Az ügyfél a kész tartalmat (copy) jóváhagyta.',
  },
  {
    bar: 'var(--warning)',
    label: 'Téma módosítás kérve',
    meaning: 'Az ügyfél módosítást kért a témán — nézd meg a kommenteket.',
  },
  {
    bar: 'var(--warning)',
    label: 'Poszt módosítás kérve',
    meaning: 'Az ügyfél módosítást kért a poszton — nézd meg a kommenteket.',
  },
  {
    bar: 'var(--st-rejected-dot)',
    label: 'Téma elutasítva',
    meaning: 'Az ügyfél elvetette a témát, újratervezés kell.',
  },
  {
    bar: 'var(--st-rejected-dot)',
    label: 'Poszt elutasítva',
    meaning: 'Az ügyfél elvetette a posztot, újratervezés kell.',
  },
  {
    bar: 'var(--st-scheduled-dot)',
    label: 'Időzítve',
    meaning: 'Betéve a Meta-ütemezőbe, még nem élő.',
  },
  {
    bar: 'var(--st-published-dot)',
    label: 'Posztolva',
    meaning: 'Már publikálva van — kiszürkül.',
  },
];

function StageLegend({ className }: { className?: string }) {
  return (
    <details className={cn('group relative', className)}>
      <summary className="flex h-9 cursor-pointer items-center gap-1.5 rounded-full border border-[var(--app-divider)] bg-[var(--app-surface)] px-3 text-meta font-medium text-[var(--fg-2)] hover:bg-[var(--app-hover)] hover:text-[var(--ink-700)]">
        <svg
          width={14}
          height={14}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.75}
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="9" />
          <path d="M12 8v.01M11 12h1v4h1" strokeLinecap="round" />
        </svg>
        Folyamat és színek
        <span className="ml-1 text-meta opacity-60 transition-transform group-open:rotate-180">
          ▾
        </span>
      </summary>
      {/* Anchored on the RIGHT: this toggle sits at the right end of the
          toolbar, so a left-anchored panel ran off the viewport and clipped
          its own text. */}
      <div className="absolute right-0 top-11 z-20 max-h-[70vh] w-[340px] overflow-y-auto rounded-lg border border-[var(--app-divider)] bg-[var(--app-surface)] p-3 shadow-lg">
        <div className="mb-2 text-meta font-semibold uppercase tracking-wider text-[var(--fg-faint)]">
          A folyamat lépései
        </div>
        <WorkflowSteps />
        <div className="mb-2 mt-3 border-t border-[var(--app-divider)] pt-2.5 text-meta font-semibold uppercase tracking-wider text-[var(--fg-faint)]">
          Poszt-állapotok színkódja
        </div>
        <ul className="space-y-2">
          {LEGEND_ENTRIES.map((e) => (
            <li key={e.label} className="flex items-start gap-2.5">
              {e.iconKey ? (
                (() => {
                  const LegendIcon = STATUS_ICON[e.iconKey];
                  return (
                    <LegendIcon
                      size={14}
                      className="mt-px shrink-0 text-[var(--st-neutral-fg)]"
                      style={e.iconColor ? { color: e.iconColor } : undefined}
                      aria-hidden="true"
                    />
                  );
                })()
              ) : (
                <span
                  className="mt-0.5 inline-block h-3 w-1 shrink-0 rounded-full"
                  style={{ background: e.bar }}
                  aria-hidden="true"
                />
              )}
              <div className="min-w-0 flex-1">
                <div className="text-body font-semibold text-[var(--ink-700)]">{e.label}</div>
                <div className="text-body leading-snug text-[var(--fg-2)]">{e.meaning}</div>
              </div>
            </li>
          ))}
        </ul>
        <div className="mt-2.5 border-t border-[var(--app-divider)] pt-2 text-meta text-[var(--fg-faint)]">
          A bal szegély a fő stádiumot, a chip a pontos státuszt mutatja.
        </div>
      </div>
    </details>
  );
}

export const Route = createFileRoute('/app/plans/$hotelId/$year/$month')({
  validateSearch: (
    s: Record<string, unknown>,
  ): { post?: string; comments?: 'client'; statusPanel?: true } => {
    const post = typeof s.post === 'string' && s.post.length > 0 ? s.post : undefined;
    if (!post) return {};
    // `comments=client`: the bell link opens the comments on the client tab.
    // `statusPanel`: the card menu opens the status panel.
    const statusPanel = s.statusPanel === true || s.statusPanel === 'true';
    return {
      post,
      ...(s.comments === 'client' ? { comments: 'client' as const } : {}),
      ...(statusPanel ? { statusPanel: true as const } : {}),
    };
  },
  component: PlanCalendarPage,
});
